<template>
  <ContentWrap>
    <el-form :inline="true" :model="query" class="-mb-15px" @submit.prevent>
      <el-form-item label="用户昵称">
        <el-input
          v-model="query.userNickname"
          clearable
          class="!w-180px"
          placeholder="请输入用户昵称"
          @keyup.enter="search"
        />
      </el-form-item>
      <el-form-item label="文章标题">
        <el-input
          v-model="query.newsTitle"
          clearable
          class="!w-220px"
          placeholder="请输入文章标题"
          @keyup.enter="search"
        />
      </el-form-item>
      <el-form-item label="评论时间">
        <el-date-picker
          v-model="commentRange"
          type="datetimerange"
          value-format="YYYY-MM-DD HH:mm:ss"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          class="!w-360px"
        />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search">
          <Icon icon="ep:search" class="mr-5px" />查询
        </el-button>
        <el-button @click="reset"><Icon icon="ep:refresh" class="mr-5px" />重置</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <div class="mb-12px">
      <el-button
        v-hasPermi="[updatePermission]"
        type="success"
        plain
        :disabled="!selectedRows.length"
        @click="batchStatus(1)"
      >
        <Icon icon="ep:circle-check" class="mr-5px" />批量启用
      </el-button>
      <el-button
        v-hasPermi="[updatePermission]"
        type="warning"
        plain
        :disabled="!selectedRows.length"
        @click="batchStatus(2)"
      >
        <Icon icon="ep:circle-close" class="mr-5px" />批量禁用
      </el-button>
      <el-button
        v-hasPermi="[deletePermission]"
        type="danger"
        plain
        :disabled="!selectedRows.length"
        @click="deleteSelected"
      >
        <Icon icon="ep:delete" class="mr-5px" />批量删除
      </el-button>
      <span v-if="selectedRows.length" class="ml-10px text-12px text-#909399">
        已选择 {{ selectedRows.length }} 项
      </span>
    </div>
    <el-table
      v-loading="loading"
      :data="list"
      row-key="id"
      @selection-change="selectedRows = $event"
    >
      <el-table-column type="selection" width="45" />
      <el-table-column prop="userNickname" label="用户昵称" min-width="140" show-overflow-tooltip />
      <el-table-column prop="newsTitle" label="文章标题" min-width="220" show-overflow-tooltip />
      <el-table-column prop="content" label="评论内容" min-width="280" show-overflow-tooltip />
      <el-table-column label="状态" width="100">
        <template #default="{ row }">
          <el-tag :type="row.status === 1 ? 'success' : row.status === 2 ? 'info' : 'warning'">
            {{ statusText(row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="createTime" label="评论时间" width="180" :formatter="dateFormatter" />
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="{ row }">
          <el-button
            v-hasPermi="[updatePermission]"
            link
            :type="row.status === 1 ? 'warning' : 'success'"
            @click="setStatus(row)"
          >
            {{ row.status === 1 ? '禁用' : '启用' }}
          </el-button>
          <el-button v-hasPermi="[deletePermission]" link type="danger" @click="remove(row.id)"
            >删除</el-button
          >
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="load"
    />
  </ContentWrap>
</template>

<script setup lang="ts">
import * as Api from '@/api/business/newsComment'
import { dateFormatter } from '@/utils/formatTime'

defineOptions({ name: 'BossCircleNewsComment' })

const message = useMessage()
const route = useRoute()
const userMode = computed(() => route.path.includes('/circle/comment'))
const updatePermission = computed(() =>
  userMode.value ? 'bosscircle:user-news-comment:update' : 'bosscircle:news-comment:update'
)
const deletePermission = computed(() =>
  userMode.value ? 'bosscircle:user-news-comment:delete' : 'bosscircle:news-comment:delete'
)
const loading = ref(false)
const list = ref<any[]>([])
const selectedRows = ref<any[]>([])
const total = ref(0)
const commentRange = ref<string[]>([])
const query = reactive({
  publisherType: 1,
  pageNo: 1,
  pageSize: 10,
  userNickname: '',
  newsTitle: ''
})
const statusText = (status: number) => ['待审核', '启用', '禁用'][status] || '未知'
const load = async () => {
  loading.value = true
  try {
    const data = await Api.getCommentPage(
      {
        ...query,
        publisherType: 1,
        createTimeStart: commentRange.value?.[0],
        createTimeEnd: commentRange.value?.[1]
      },
      userMode.value
    )
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}
const search = () => {
  query.pageNo = 1
  load()
}
const reset = () => {
  query.pageNo = 1
  query.userNickname = ''
  query.newsTitle = ''
  commentRange.value = []
  load()
}
const setStatus = async (row: any) => {
  const status = row.status === 1 ? 2 : 1
  await message.confirm(`确认${status === 1 ? '启用' : '禁用'}该评论吗？`)
  await Api.updateCommentStatus(row.id, status, userMode.value)
  message.success('操作成功')
  await load()
}
const batchStatus = async (status: number) => {
  await message.confirm(
    `确认${status === 1 ? '启用' : '禁用'}选中的 ${selectedRows.value.length} 条评论吗？`
  )
  await Api.batchUpdateCommentStatus(
    selectedRows.value.map((item) => item.id),
    status,
    userMode.value
  )
  selectedRows.value = []
  message.success('批量操作成功')
  await load()
}
const remove = async (id: number) => {
  await message.delConfirm('确认删除该评论吗？')
  await Api.deleteComment(id, userMode.value)
  message.success('删除成功')
  await load()
}
const deleteSelected = async () => {
  await message.delConfirm(`确认删除选中的 ${selectedRows.value.length} 条评论吗？`)
  await Api.batchDeleteComment(
    selectedRows.value.map((item) => item.id),
    userMode.value
  )
  selectedRows.value = []
  message.success('批量删除成功')
  await load()
}

onMounted(load)
</script>
